import { createSlice } from '@reduxjs/toolkit';
import { placesApi } from '@store/rtk/places';
import { idType, selectItemType } from '@src/types';

export type LocationItemData = {
  id: number;
  activeRecordIndicator: boolean;
  faxNumber: string;
  location: {
    address1: string;
    address2: string;
    city: selectItemType;
    countyId: idType;
    id: idType;
    latitude: number | null;
    longitude: number | null;
    state: selectItemType;
    type: selectItemType;
    verified: boolean;
    zipCode: selectItemType;
  } | null;
  locationsType: { id: idType; name: string };
  payorId: boolean;
  places: {
    companyName: string;
    email: string;
    faxNumber: string;
    id: number;
    locationId: number;
    primaryExt: string;
    mailingAddress: number;
    primaryNumber: string;
    secondaryExt: string;
    secondaryNumber: string;
    taxNumber: string;
    website: string;
  };
  primaryExt: string;
  primaryNumber: string;
  secondaryExt: string;
  secondaryNumber: string;
  streetMapDocumentRecordId: string;
};

type PlacesInfoDataType = {
  id: number;
  website: string | null;
  logos: string | null;
  companyName: string | null;
  taxNumber: string | null;
  primaryNumber: string | null;
  secondaryNumber: string | null;
  primaryExt: string | null;
  secondaryExt: string | null;
  faxNumber: string | null;
  email: string | null;
  linkingNumber: string | null;
  logo_document_id: string | null;
  companyType: string | null;
  // TODO: review and delete if no longer need
  primaryLocation: LocationType | null;
  physicalAddress: LocationType | null;
  mailingAddress: LocationType | null;
};

type LocationType = {
  id: idType;
  latitude: number | null;
  longitude: number | null;
  verified: boolean;
  countyId: idType;
  address1: string | null;
  address2: string | null;
  typeId: idType;
  state: selectItemType;
  city: selectItemType;
  zipCode: selectItemType;
};

type LocationHoursDataType = {
  id: idType;
  covered_indicator?: boolean;
  table_name?: string | null;
  field_name?: string | null;
  weekDay?: {
    id: idType;
    name: string;
  };
  time?: {
    id: idType;
    name: string;
  };
  associated_id?: number;
};

interface PlacesInfoState {
  placesData: PlacesInfoDataType | null;
  locationsData: Array<LocationItemData> | null;
  locationHoursData: Array<LocationHoursDataType> | null;
  locationSearch: any;
  isPlacesSaved: boolean;
}

const initialState: PlacesInfoState = {
  placesData: null,
  locationsData: null,
  locationHoursData: null,
  locationSearch: null,
  isPlacesSaved: true,
};

export const placesInfoSlice = createSlice({
  name: 'placesInfo',
  initialState,
  reducers: {
    setLocationData: (state, { payload: { id, value } }) => {
      state.locationsData = [
        ...(state.locationsData || []).map((place) => {
          if (place.id === id) {
            return value;
          }
          return place;
        }),
      ];
    },
    setIsPlacesSaved: (state, { payload }) => {
      state.isPlacesSaved = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      placesApi.endpoints.getPlacesInfo.matchFulfilled,
      (state, { payload }) => {
        state.placesData = payload;
      }
    );
    builder.addMatcher(
      placesApi.endpoints.getPlacesLocation.matchFulfilled,
      (state, { payload }) => {
        state.locationsData = payload;
      }
    );
    builder.addMatcher(
      placesApi.endpoints.getPlacesSearchLocation.matchFulfilled,
      (state, { payload }) => {
        state.locationSearch = payload;
      }
    );
  },
});

export const { setLocationData, setIsPlacesSaved } = placesInfoSlice.actions;

export const selectPlaces = (state: { placesInfo: PlacesInfoState }) =>
  state.placesInfo;
export const placesInfoReducer = placesInfoSlice.reducer;
